import styled from '@emotion/styled';
import React, { FC, useState } from 'react';
import { useCashApi, useCashUniverse } from '~Components/CashUniverse';
import { useStorage } from '~Components/Storage';
import useModal from '~ComponentsImproved/Modal/useModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useCurrency, useI18n, useSelector } from '~Hooks';
import { GetVenuesCash_venue_cashSources } from '~Scenes/CashSources/__queries__';
import { Box } from '~Styles';
import CashSourceComponent from '../CashSource';
import { DateTime } from 'luxon';
import { PrintRow } from '~Components/PrinterManager';
import MessageInput from '~ComponentsImproved/MessageInput';
import Modal from '~ComponentsImproved/Modal';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
`;

interface Props {
  cashSources: GetVenuesCash_venue_cashSources[];
}

/** List cash sources & their balance with option to withdraw/deposit */
const CashSourceManager: FC<Props> = ({ cashSources }) => {
  const { i18n } = useI18n();

  const api = useCashApi();
  const cashUniverse = useCashUniverse();
  const currency = useCurrency();
  const cashChangeReasonModal = useModal();
  const [storage] = useStorage();

  const country = useSelector((state) => state.country);
  const staff = useSelector((state) => state.staff);

  const [pendingUpdate, setPendingUpdate] = useState<{ amount: number; sourceId?: string } | null>(
    null,
  );

  const handleUpdate = (amount: number, sourceId?: string | undefined, reason?: string) => {
    if (reason === undefined) {
      // Store the pending update and show modal
      setPendingUpdate({ amount, sourceId });
      cashChangeReasonModal.show();
      return;
    }

    // Process the actual update
    const isWithdrawal = amount < 0;

    /* Print a receipt-like print */
    window.printerManager?.initiatePrint({
      printType: 'CASH_CHANGE',
      data: [
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        {
          content: isWithdrawal
            ? i18n('CashManagement.CashWithdrawal')
            : i18n('CashManagement.CashDeposit'),
          fontSize: 28,
          alignment: 'CENTER',
        },
        { content: DateTime.now().toFormat('HH:mm dd-MM-yy'), alignment: 'CENTER' },
        {
          content: `${i18n('Menu.Device')}: ${storage.taxComplianceDeviceName ||
            'Unknown device'}`,
          alignment: 'CENTER',
        },
        staff && {
          content: `${i18n('ParkedOrders.Staff')}: ${staff.name}`,
          alignment: 'CENTER',
        },
        reason && {
          content: `${i18n('CashManagement.Change.Reason')}: ${reason}`,
          alignment: 'CENTER',
        },
        { content: ' ' },
        {
          content: `${sourceId}: ` + currency.format(amount),
          alignment: 'CENTER',
        },
        { content: ' ' },
        { content: ' ' },
      ].filter(Boolean) as PrintRow[],
    });

    api.updateCash(amount, sourceId);
  };

  return (
    <>
      <Wrapper display="flex" flexDirection="column" padding="medium" gap="xs">
        <Heading level={3} color="var(--colorWhite)" bottom={8}>
          {i18n('CashManagement.Tenders')}
        </Heading>

        <Box display="flex" flexDirection="column" gap="medium">
          <CashSourceComponent
            cashSource={{
              id: 'DRAWER',
              name: i18n('CashManagement.CashRegister'),
              __typename: 'CashSource',
            }}
            currentBalance={cashUniverse.totalCashRegister || 0}
            handleUpdate={handleUpdate}
          />

          {cashSources.map((source) => {
            const sourceData = cashUniverse.sources.find((s) => s.id === source.id);
            const added = sourceData?.added || 0;
            const removed = sourceData?.removed || 0;
            return (
              <CashSourceComponent
                key={source.id}
                cashSource={source}
                handleUpdate={handleUpdate}
                currentBalance={added - removed}
              />
            );
          })}
        </Box>
      </Wrapper>

      <Modal isShowing={cashChangeReasonModal.isShowing} hide={cashChangeReasonModal.hide}>
        <MessageInput
          type="text"
          placeHolder={i18n('CashManagement.Change.EnterReason')}
          acceptEmpty={country !== 'ES'}
          onSubmit={(reason: string) => {
            if (pendingUpdate) {
              handleUpdate(pendingUpdate.amount, pendingUpdate.sourceId, reason);
              setPendingUpdate(null);
            }
            cashChangeReasonModal.hide();
          }}
          width="500px"
        />
      </Modal>
    </>
  );
};

export default CashSourceManager;
